import * as React from "react"

import {
    ToolboxRichTextField,
    RichTextField as RTF,
} from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"

import { IRichTextField } from "../.."
import { updateField } from "../../slice"
import { addErrorNotification } from "../../../notifications/actions"
import { addAttachment } from "./slice"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../util/Translations"

export interface OwnProps {
    field: IRichTextField
}

type Props = OwnProps

export const RichTextField = ({ field }: Props): JSX.Element => {
    const { ClickHereToEdit, TextExceedsMaximumLength } = Labels
    const maximumCharactersExceededMessage = "maxCharactersExceeded"
    const dispatch = useAppDispatch()
    const [headerSize, setHeaderSize] = React.useState({ width: 0, height: 0 })

    React.useEffect(() => {
        const element = document.querySelector("header.NewUI-item-header")

        if (!element) {
            return
        }

        const { offsetWidth: width, offsetHeight: height } =
            element as HTMLElement
        setHeaderSize({ width, height })

        const handleResize = (entries: ResizeObserverEntry[]): void => {
            for (const entry of entries) {
                if (entry.target === element) {
                    setHeaderSize({
                        width: entry.contentRect.width,
                        height: entry.contentRect.height,
                    })
                }
            }
        }

        const resizeObserver = new ResizeObserver(handleResize)
        resizeObserver.observe(element)

        return () => {
            resizeObserver.unobserve(element)
            resizeObserver.disconnect()
        }
    }, [])

    const { isLoading } = useAppSelector((state: GlobalState) => state.fields)
    const isRichTextLoading = useAppSelector(
        (state: GlobalState) => state.richText.isLoading,
    )
    const { loadingResources } = useAppSelector(
        (state: GlobalState) => state.resources,
    )

    const {
        odataType: _odataType,
        value,
        description,
        itemId,
        maxLength,
        ...fieldProps
    } = field

    const isFieldLoading = isLoading || loadingResources.length > 0

    const handleOnSave = React.useCallback(
        (newValue: string): void => {
            if (newValue === value) {
                return
            }
            dispatch(
                updateField({
                    field,
                    newValue,
                }),
            )
        },
        [value, dispatch, field, updateField],
    )
    const handleError = React.useCallback(
        (error: Error): void => {
            if (error && error.message === maximumCharactersExceededMessage) {
                console.error(error)
            }
            const errorMessage = `${
                TextExceedsMaximumLength ||
                "Maximum characters exceeded in input for"
            }: ["${field.title}"].`
            dispatch(addErrorNotification(errorMessage))
        },
        [dispatch, field, addErrorNotification],
    )

    const onFilesAdded = React.useCallback(
        async (files: File[]): Promise<ToolboxRichTextField.Attachment[]> => {
            //Await for the result here because it is immediately returned to the child component.
            const results = await dispatch(addAttachment({ files, field }))
            const fileResults =
                (results.payload as ToolboxRichTextField.Attachment[]) || []
            return fileResults
        },
        [addAttachment, dispatch, field],
    )

    let displayValue: undefined | JSX.Element = undefined
    if (!value) {
        displayValue = undefined
    } else {
        displayValue = <HTMLAndInternalLinkParser content={value ?? ""} />
    }

    let displayDescription: string | JSX.Element | undefined = ""
    if (typeof description === "string") {
        displayDescription = (
            <HTMLAndInternalLinkParser content={description ?? ""} />
        )
    } else {
        displayDescription = description
    }

    return (
        <RTF
            {...fieldProps}
            isLoading={isFieldLoading || isRichTextLoading}
            onSave={handleOnSave}
            extraClasses="tw-w-full"
            placeholder={fieldProps.isReadOnly ? "" : ClickHereToEdit}
            value={value || ""}
            isSourceEditingEnabled={false}
            onToolboxError={handleError}
            onFilesAdded={onFilesAdded}
            isFileUploadEnabled={true}
            parsedValue={displayValue}
            description={displayDescription}
            stickyToolbarOffset={headerSize.height}
            maxLength={maxLength}
        />
    )
}

export default RichTextField
